import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONTROLLER_TYPES, type ControllerType } from '@bridgewerk/shared';
import { PageContainer } from '../../components/layout/PageContainer.js';
import { Button } from '../../components/ui/Button.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useToast } from '../../components/ui/Toast.js';
import { useAuth } from '../auth/useAuth.js';
import { getApiClient } from '../../lib/api-client.js';
import { machineKeys } from '../../lib/query-keys.js';
import { useCreateMachine, useDeleteMachine } from '../machines/hooks/useMachineMutations.js';

const CONTROLLER_LABELS: Record<ControllerType, string> = {
  fanuc_0i_md:    'FANUC 0i-MD',
  fanuc_30i:      'FANUC 30i',
  mazak_matrix:   'Mazak Matrix',
  mazak_smoothx:  'Mazak SmoothX',
  haas_nextgen:   'Haas Next Gen',
  mori_mapps:     'Mori MAPPS',
  okuma_osp:      'Okuma OSP',
  siemens_840d:   'Siemens 840D',
  heidenhain_tnc: 'Heidenhain TNC',
  unknown:        'Unknown / Other',
};

function useShopName(userId: string | null): [string, (n: string) => void] {
  const key = userId ? `werkos:shop_name:${userId}` : null;
  const [name, setName] = useState(() => (key ? (window.localStorage.getItem(key) ?? '') : ''));
  const save = (n: string) => {
    if (!key) return;
    window.localStorage.setItem(key, n);
    setName(n);
  };
  return [name, save];
}

export function SettingsPage(): JSX.Element {
  const { user, shopId } = useAuth();
  const [shopName, saveShopName] = useShopName(user?.sub ?? null);
  const [nameInput, setNameInput] = useState(shopName);
  const [nameSaved, setNameSaved] = useState(false);

  const handleSaveName = () => {
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    saveShopName(trimmed);
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2000);
  };

  return (
    <PageContainer>
      <div className="mb-6">
        <h1 className="font-mono text-base">Settings</h1>
        <p className="text-xs text-text-dim mt-0.5">Shop configuration and account.</p>
      </div>

      <div className="max-w-xl space-y-6">
        <Section title="Shop">
          <Field label="Display name">
            <div className="flex gap-2">
              <input
                type="text"
                value={nameInput}
                onChange={e => { setNameInput(e.target.value); setNameSaved(false); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); }}
                placeholder="My Shop"
                maxLength={40}
                className="flex-1 rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-text placeholder:text-muted focus:border-accent focus:outline-none"
              />
              <Button variant="primary" onClick={handleSaveName}>
                {nameSaved ? 'Saved ✓' : 'Save'}
              </Button>
            </div>
            <p className="text-[10px] font-mono text-muted mt-1.5">Shown as your avatar initials throughout the app.</p>
          </Field>
          <Field label="Shop ID">
            <p className="text-sm font-mono text-text-dim px-3 py-2 rounded-md border border-border bg-card/50 select-all">
              {shopId ?? '—'}
            </p>
          </Field>
        </Section>

        <Section title="Machines">
          <MachinesSection shopId={shopId} />
        </Section>

        <Section title="Account">
          <Field label="Email">
            <p className="text-sm font-mono text-text-dim px-3 py-2 rounded-md border border-border bg-card/50">
              {user?.email ?? '—'}
            </p>
          </Field>
          <Field label="Password">
            <p className="text-[11px] font-mono text-muted">Password is managed through your identity provider.</p>
          </Field>
        </Section>

        <Section title="Integrations">
          <div className="px-3 py-4 rounded-md border border-dashed border-border text-center">
            <p className="text-xs font-mono text-text-dim">IoT hub pairing, webhooks, and API keys — coming soon.</p>
          </div>
        </Section>
      </div>
    </PageContainer>
  );
}

// ── Machines section ──────────────────────────────────────────────────────────

function MachinesSection({ shopId }: { readonly shopId: string | null }): JSX.Element {
  const toast = useToast();
  const [showAdd, setShowAdd] = useState(false);
  const [machineId, setMachineId] = useState('');
  const [controllerType, setControllerType] = useState<ControllerType>('fanuc_0i_md');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: machines, isLoading } = useQuery({
    queryKey: shopId ? machineKeys.list(shopId) : ['machines', 'none'],
    queryFn: () => getApiClient().getMachines(shopId!),
    enabled: !!shopId,
    staleTime: 30_000,
  });

  const create = useCreateMachine(shopId);
  const remove = useDeleteMachine(shopId);

  const handleAdd = async () => {
    const id = machineId.trim();
    if (!id || !shopId) return;
    try {
      await create.mutateAsync({ machineId: id, controllerType });
      toast.push({ tone: 'success', title: 'Machine added', description: id });
      setMachineId('');
      setShowAdd(false);
    } catch (err) {
      toast.push({ tone: 'critical', title: 'Could not add machine', description: err instanceof Error ? err.message : String(err) });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await remove.mutateAsync(id);
      toast.push({ tone: 'success', title: 'Machine removed', description: id });
      setDeletingId(null);
    } catch (err) {
      toast.push({ tone: 'critical', title: 'Could not remove', description: err instanceof Error ? err.message : String(err) });
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-2">
      {isLoading && (
        <div className="space-y-2">
          {[0, 1].map(i => <Skeleton key={i} className="h-12 rounded-md" />)}
        </div>
      )}

      {!isLoading && machines && machines.length === 0 && (
        <p className="text-xs font-mono text-muted py-2">No machines registered yet.</p>
      )}

      {!isLoading && machines && machines.map(m => (
        <div key={m.machine_id} className="flex items-center justify-between px-3 py-2.5 rounded-md border border-border bg-card">
          <div>
            <p className="text-sm font-mono text-text">{m.machine_id}</p>
            <p className="text-[10px] font-mono text-text-dim">{CONTROLLER_LABELS[m.controller_type]}</p>
          </div>
          {deletingId === m.machine_id ? (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-text-dim">Remove?</span>
              <button
                type="button"
                onClick={() => void handleDelete(m.machine_id)}
                disabled={remove.isPending}
                className="text-[11px] font-mono text-red hover:underline disabled:opacity-50"
              >
                {remove.isPending ? 'Removing…' : 'Yes'}
              </button>
              <button
                type="button"
                onClick={() => setDeletingId(null)}
                className="text-[11px] font-mono text-text-dim hover:text-text"
              >
                No
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDeletingId(m.machine_id)}
              className="text-[11px] font-mono text-muted hover:text-red transition-colors"
            >
              Remove
            </button>
          )}
        </div>
      ))}

      {showAdd ? (
        <div className="rounded-md border border-accent/30 bg-accent/5 p-3 space-y-2.5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-accent">Add machine</p>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] font-mono text-text-dim block mb-1">Machine ID</label>
              <input
                type="text"
                value={machineId}
                onChange={e => setMachineId(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void handleAdd(); if (e.key === 'Escape') setShowAdd(false); }}
                placeholder="e.g. HAAS-01"
                maxLength={64}
                pattern="[a-zA-Z0-9_-]+"
                autoFocus
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-text placeholder:text-muted focus:border-accent focus:outline-none"
              />
              <p className="text-[9px] font-mono text-muted mt-1">Letters, numbers, hyphens, underscores only.</p>
            </div>
            <div>
              <label className="text-[10px] font-mono text-text-dim block mb-1">Controller</label>
              <select
                value={controllerType}
                onChange={e => setControllerType(e.target.value as ControllerType)}
                className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-text focus:border-accent focus:outline-none"
              >
                {CONTROLLER_TYPES.map(ct => (
                  <option key={ct} value={ct}>{CONTROLLER_LABELS[ct]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              variant="primary"
              onClick={() => void handleAdd()}
              disabled={!machineId.trim() || create.isPending}
            >
              {create.isPending ? 'Adding…' : 'Add machine'}
            </Button>
            <Button variant="ghost" onClick={() => { setShowAdd(false); setMachineId(''); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="w-full rounded-md border border-dashed border-border py-2.5 text-xs font-mono text-text-dim hover:border-accent hover:text-accent transition-colors"
        >
          + Add machine
        </button>
      )}
    </div>
  );
}

// ── Layout helpers ────────────────────────────────────────────────────────────

function Section({ title, children }: { readonly title: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-dim mb-3">{title}</h2>
      <div className="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
        <div className="px-4 py-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-mono uppercase tracking-widest text-muted block">{label}</label>
      {children}
    </div>
  );
}
